import { useState } from "react";
import { Upload, CheckCircle2, Clock, AlertCircle, Eye, Trash2, FolderOpen, Download } from "lucide-react";
import { motion } from "motion/react";
import { MOCK_DOCUMENTS, Document, DocStatus } from "../../data/mockData";

const CATEGORIES = ["All", "Identity", "Academic", "Financial", "Others"];

const STATUS_CONFIG: Record<DocStatus, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  verified: { label: "Verified",  color: "text-green-700",  bg: "bg-green-50",  border: "border-green-200", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  pending:  { label: "Pending",   color: "text-amber-700",  bg: "bg-amber-50",  border: "border-amber-200", icon: <Clock className="w-3.5 h-3.5" /> },
  needed:   { label: "Required",  color: "text-red-700",    bg: "bg-red-50",    border: "border-red-200",   icon: <AlertCircle className="w-3.5 h-3.5" /> },
};

const FILE_ICONS: Record<string, string> = {
  "Identity": "🪪",
  "Academic": "🎓",
  "Financial": "💳",
  "Others": "📄",
};

function UploadZone({ onUpload }: { onUpload: () => void }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); onUpload(); }}
      className={`border-2 border-dashed rounded-3xl p-8 text-center transition-all duration-200 cursor-pointer ${
        dragging ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50/50"
      }`}
      onClick={onUpload}
    >
      <div className={`w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center transition-colors ${dragging ? "bg-violet-100" : "bg-white border border-slate-200"}`}>
        <Upload className={`w-5 h-5 ${dragging ? "text-violet-600" : "text-slate-400"}`} />
      </div>
      <p className="text-[14px] text-slate-700 mb-1" style={{ fontWeight: 600 }}>
        {dragging ? "Drop to upload" : "Drag & drop your file here"}
      </p>
      <p className="text-[12px] text-slate-500 mb-3" style={{ fontWeight: 400 }}>
        Supports PDF, JPG, PNG — Max 10MB per file
      </p>
      <button className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] shadow-md" style={{ fontWeight: 600 }}>
        Browse Files
      </button>
    </div>
  );
}

function DocumentCard({ doc, onUpload }: { doc: Document; onUpload: (id: string) => void }) {
  const cfg = STATUS_CONFIG[doc.status];

  return (
    <motion.div layout
      className={`bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all duration-200 ${
        doc.status === "needed" ? "border-red-100" : "border-slate-100"
      }`}>
      {/* Icon */}
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-[22px] flex-shrink-0 ${
        doc.status === "verified" ? "bg-green-50" : doc.status === "pending" ? "bg-amber-50" : "bg-red-50"
      }`}>
        {FILE_ICONS[doc.category]}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-[13px] text-slate-900 truncate" style={{ fontWeight: 600 }}>{doc.name}</p>
          {doc.required && doc.status === "needed" && (
            <span className="text-[10px] text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full flex-shrink-0" style={{ fontWeight: 700 }}>Required</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${cfg.color} ${cfg.bg} ${cfg.border}`} style={{ fontWeight: 600 }}>
            {cfg.icon} {cfg.label}
          </span>
          {doc.uploadedAt && (
            <span className="text-[11px] text-slate-400" style={{ fontWeight: 400 }}>
              {doc.uploadedAt} · {doc.size}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {doc.status !== "needed" ? (
          <>
            <button className="w-8 h-8 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 hover:border-violet-100 transition-all">
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button className="w-8 h-8 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 hover:border-violet-100 transition-all">
              <Download className="w-3.5 h-3.5" />
            </button>
            <button className="w-8 h-8 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <button onClick={() => onUpload(doc.id)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[12px] hover:bg-red-100 transition-colors" style={{ fontWeight: 600 }}>
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function DocumentsPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [docs, setDocs] = useState<Document[]>(MOCK_DOCUMENTS);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const filtered = docs.filter(d => activeCategory === "All" || d.category === activeCategory);

  const verified = docs.filter(d => d.status === "verified").length;
  const pending = docs.filter(d => d.status === "pending").length;
  const needed = docs.filter(d => d.status === "needed").length;
  const storageUsed = docs.filter(d => d.size).reduce((acc, d) => acc + parseFloat(d.size || "0"), 0);

  const handleUpload = (id?: string) => {
    if (id) {
      setDocs(prev => prev.map(d => d.id === id ? { ...d, status: "pending" as DocStatus, uploadedAt: "Just now", size: "1.2 MB" } : d));
    }
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 2500);
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-[28px] text-slate-900 tracking-tight" style={{ fontWeight: 900 }}>Documents</h1>
        <p className="text-[14px] text-slate-500 mt-1" style={{ fontWeight: 400 }}>
          Keep all your documents organised and verified in one secure place.
        </p>
      </motion.div>

      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Verified", value: verified, color: "text-green-600", bg: "bg-green-50", icon: <CheckCircle2 className="w-4 h-4" /> },
          { label: "Pending Review", value: pending, color: "text-amber-600", bg: "bg-amber-50", icon: <Clock className="w-4 h-4" /> },
          { label: "Required", value: needed, color: "text-red-600", bg: "bg-red-50", icon: <AlertCircle className="w-4 h-4" /> },
          { label: "Storage Used", value: `${storageUsed.toFixed(1)} MB`, color: "text-violet-600", bg: "bg-violet-50", icon: <FolderOpen className="w-4 h-4" /> },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center ${stat.color}`}>{stat.icon}</div>
            <div>
              <p className={`text-[20px] leading-none ${stat.color}`} style={{ fontWeight: 900 }}>{stat.value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5" style={{ fontWeight: 500 }}>{stat.label}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Required docs alert */}
      {needed > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 mb-6">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[13px] text-red-800" style={{ fontWeight: 600 }}>
              {needed} required {needed === 1 ? "document is" : "documents are"} missing
            </p>
            <p className="text-[12px] text-red-600" style={{ fontWeight: 400 }}>Upload now to avoid delays with your applications.</p>
          </div>
        </motion.div>
      )}

      {/* Upload success */}
      {uploadSuccess && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="flex items-center gap-3 p-4 rounded-2xl bg-green-50 border border-green-200 mb-6">
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-[13px] text-green-800" style={{ fontWeight: 600 }}>Document uploaded! We'll verify it within 24 hours.</p>
        </motion.div>
      )}

      {/* Upload zone */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
        <UploadZone onUpload={() => handleUpload()} />
      </motion.div>

      {/* Category filter */}
      <div className="flex items-center gap-1 bg-white rounded-2xl border border-slate-100 p-1.5 shadow-sm mb-5 overflow-x-auto w-fit">
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-xl text-[13px] whitespace-nowrap transition-all ${activeCategory === cat ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
            style={{ fontWeight: activeCategory === cat ? 700 : 500 }}>
            {cat}
            <span className={`ml-1.5 text-[11px] ${activeCategory === cat ? "text-violet-200" : "text-slate-400"}`}>
              {cat === "All" ? docs.length : docs.filter(d => d.category === cat).length}
            </span>
          </button>
        ))}
      </div>

      {/* Document list */}
      <div className="flex flex-col gap-3">
        {CATEGORIES.filter(c => c !== "All" && (activeCategory === "All" || activeCategory === c)).map(category => {
          const catDocs = filtered.filter(d => d.category === category);
          if (catDocs.length === 0) return null;
          return (
            <div key={category}>
              {activeCategory === "All" && (
                <div className="flex items-center gap-2 mb-3 mt-2">
                  <span className="text-[16px]">{FILE_ICONS[category]}</span>
                  <h3 className="text-[13px] text-slate-700" style={{ fontWeight: 700 }}>{category}</h3>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                {catDocs.map(doc => (
                  <motion.div key={doc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <DocumentCard doc={doc} onUpload={handleUpload} />
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="mt-8 bg-violet-50 rounded-3xl border border-violet-100 p-6">
        <p className="text-[14px] text-violet-900 mb-3" style={{ fontWeight: 700 }}>📌 Document Tips from Kak Sarah</p>
        <div className="flex flex-col gap-2">
          {[
            "Always upload clear, colour scans — blurry images get rejected.",
            "Ensure your IC/Passport is valid for at least 6 months beyond your intended intake.",
            "Recommendation letters must be on official letterhead with a signature.",
          ].map((tip, i) => (
            <p key={i} className="text-[12px] text-violet-700 flex gap-2 items-start" style={{ fontWeight: 400 }}>
              <span className="flex-shrink-0 mt-0.5">→</span> {tip}
            </p>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
